import { useEffect, useState } from "react";
import { ChevronDown, RefreshCcw, Trash2 } from "lucide-react";
import { fetchMeUser } from "../../../services/user";
import { createHiddenFieldTemplateAPI, deleteHiddenFieldTemplateAPI, fetchHiddenFieldGroupsAPI, fetchHiddenFieldStaffUsersAPI, fetchHiddenFieldTemplatesAPI, syncHiddenFieldStaffUsersAPI, updateHiddenFieldGroupsAPI, updateHiddenFieldTemplateStaffAPI } from "../../../services/hidden_fields";

const HiddenFields = () => {
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const [templateGroupsData, setTemplateGroupsData] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState({});
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const responseData = await fetchMeUser();
        const locationList = String(responseData?.data?.locationId || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

        setLocations(locationList);
        setLocationId(locationList[0] || "");
      } catch (err) {
        setError(err?.message || "Unable to load current user.");
      }
    };

    loadCurrentUser();
  }, []);

  const handleSync = async () => {
    if (!locationId) return;
    setSyncing(true);
    setError(null);
    try {
      await syncHiddenFieldStaffUsersAPI(locationId);
      await Promise.all([fetchStaffUsers(), fetchTemplates()]);
    } catch (err) {
      setError(err?.message || "Error syncing staff users.");
    } finally {
      setSyncing(false);
    }
  };

  const fetchStaffUsers = async () => {
    if (!locationId) return;
    setLoadingUsers(true);
    setError(null);
    try {
      const res = await fetchHiddenFieldStaffUsersAPI(locationId);
      setStaffUsers(res?.success ? res.data || [] : []);
    } catch (err) {
      setStaffUsers([]);
      setError(err?.message || "Error fetching staff users.");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTemplates = async () => {
    if (!locationId) return;
    setLoadingTemplates(true);
    setError(null);
    try {
      const res = await fetchHiddenFieldTemplatesAPI(locationId);
      setTemplates(res?.success ? res.data || [] : []);
    } catch (err) {
      setTemplates([]);
      setError(err?.message || "Error fetching templates.");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchTemplateGroups = async (templateId) => {
    if (!locationId || !templateId) return;
    setLoadingGroups((prev) => ({ ...prev, [templateId]: true }));
    setError(null);
    try {
      const res = await fetchHiddenFieldGroupsAPI(locationId, templateId);
      const groups = res.groups || [];
      setTemplateGroupsData((prev) => ({
        ...prev,
        [templateId]: groups.map((g) => ({
          ...g,
          open: false,
          fields: (g.fields || []).map((f) => ({ ...f, isVisible: f.isVisible ?? true })),
        })),
      }));
    } catch (err) {
      setTemplateGroupsData((prev) => ({ ...prev, [templateId]: [] }));
      setError(err?.message || "Error fetching groups and fields.");
    } finally {
      setLoadingGroups((prev) => ({ ...prev, [templateId]: false }));
    }
  };

  useEffect(() => {
    setExpandedTemplates({});
    setTemplateGroupsData({});
    fetchStaffUsers();
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const toggleTemplate = (templateId) => {
    const willOpen = !expandedTemplates[templateId];
    setExpandedTemplates((prev) => ({ ...prev, [templateId]: willOpen }));
    if (willOpen) fetchTemplateGroups(templateId);
  };

  const toggleGroup = (templateId, groupKey) => {
    setTemplateGroupsData((prev) => ({
      ...prev,
      [templateId]: (prev[templateId] || []).map((g) => (g.groupKey === groupKey ? { ...g, open: !g.open } : g)),
    }));
  };

  const handleAddTemplate = async () => {
    const templateName = newTemplateName.trim();
    if (!templateName || !locationId) return;

    setSavingTemplate(true);
    setError(null);
    try {
      await createHiddenFieldTemplateAPI({ group_name: templateName, locationId });
      setNewTemplateName("");
      setShowAddTemplate(false);
      fetchTemplates();
    } catch (err) {
      setError(err?.message || "Error adding template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTemplateId) return;

    setDeletingTemplate(true);
    setError(null);
    try {
      await deleteHiddenFieldTemplateAPI(deleteTemplateId);
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTemplateId));
      setDeleteTemplateId(null);
    } catch (err) {
      setError(err?.message || "Error deleting template.");
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleStaffTemplateChange = async (templateId, staffId, checked) => {
    const staffUser = staffUsers.find((u) => u.staff_id === staffId);

    setTemplates((prev) =>
      prev.map((template) => {
        const filteredStaff = (template.staff || []).filter((staff) => staff.staff_id !== staffId);
        if (Number(template.id) === Number(templateId) && checked && staffUser) {
          return { ...template, staff: [...filteredStaff, staffUser] };
        }
        return { ...template, staff: filteredStaff };
      }),
    );

    setError(null);
    try {
      await updateHiddenFieldTemplateStaffAPI({
        templateId,
        staffId,
        checked,
        locationId,
      });
      fetchTemplates();
    } catch (err) {
      setError(err?.message || "Error updating template staff.");
      fetchTemplates();
    }
  };

  const handleFieldVisibilityChange = async (templateId, groupKey, fieldKey, newValue) => {
    setTemplateGroupsData((prev) => ({
      ...prev,
      [templateId]: (prev[templateId] || []).map((g) => (g.groupKey === groupKey ? { ...g, fields: g.fields.map((f) => (f.fieldKey === fieldKey ? { ...f, isVisible: newValue } : f)) } : g)),
    }));

    setError(null);
    try {
      await updateHiddenFieldGroupsAPI({
        locationId,
        templateId,
        groupKey,
        fieldKey,
        isVisible: newValue,
      });
    } catch (err) {
      setError(err?.message || "Error updating field visibility.");
      fetchTemplateGroups(templateId);
    }
  };

  const handleGroupVisibilityChange = async (templateId, group, newValue) => {
    setTemplateGroupsData((prev) => ({
      ...prev,
      [templateId]: (prev[templateId] || []).map((g) => (g.groupKey === group.groupKey ? { ...g, isVisible: newValue, fields: g.fields.map((f) => ({ ...f, isVisible: newValue })) } : g)),
    }));

    setError(null);
    try {
      await updateHiddenFieldGroupsAPI({
        locationId,
        templateId,
        groupKey: group.groupKey,
        fieldKeys: (group.fields || []).map((field) => ({ fieldKey: field.fieldKey, fieldLabel: field.fieldLabel })),
        isVisible: newValue,
      });
    } catch (err) {
      setError(err?.message || "Error updating group visibility.");
      fetchTemplateGroups(templateId);
    }
  };

  if (!locationId) {
    return <div className="px-5 pt-10 text-center text-xl">No GHL location found for this account.</div>;
  }

  return (
    <div className="px-5">
      <div className="bg-back-dark my-4 px-4 py-4 rounded-lg flex items-center justify-between gap-4 border border-color-lines">
        <div>
          <h1 className="font-semibold text-white text-lg">Hidden Fields</h1>
          <p className="text-sm text-slate-400">Manage staff templates and visible GHL field groups.</p>
        </div>
        <div className="flex items-center gap-3">
          {locations.length > 1 && (
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className="bg-slate-900 text-white border border-color-lines rounded-lg px-3 py-2">
              {locations.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
          )}
          <button onClick={() => setShowAddTemplate(true)} className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
            Add New Template
          </button>
        </div>
      </div>

      {error && <div className="mb-4 text-red-500">{error}</div>}

      {loadingTemplates || loadingUsers ? (
        <div className="flex items-center justify-center mt-10">
          <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-10 w-10 opacity-90"></div>
        </div>
      ) : (
        <div className="mt-4 grid">
          {templates.map((template) => {
            const isOpen = !!expandedTemplates[template.id];
            const assignedStaff = template.staff || [];
            const groups = templateGroupsData[template.id] || [];

            return (
              <div key={template.id} className="rounded-lg shadow-sm mb-3 bg-back-dark overflow-hidden border border-color-lines">
                <div className="flex items-center justify-between px-4 py-3 gap-4">
                  <button onClick={() => toggleTemplate(template.id)} className="flex items-center space-x-2 text-white cursor-pointer">
                    <ChevronDown size={18} className={`ease-in-out duration-150 ${isOpen ? "-rotate-90" : "rotate-0"}`} />
                    <h2 className="font-semibold text-md">{template.group_name}</h2>
                  </button>
                  <div className="text-sm text-slate-400 text-right flex gap-4 items-center">
                    <span>{assignedStaff.length ? assignedStaff.map((staff) => staff.staff_name).join(", ") : "No staff assigned"}</span>
                    <Trash2 size={18} className="text-red-500 cursor-pointer hover:text-red-700" onClick={() => setDeleteTemplateId(template.id)} />
                  </div>
                </div>

                <div className={`transition-all duration-300 ${isOpen ? "max-h-auto p-4" : "max-h-0 p-0"} overflow-hidden`}>
                  <div className="mb-5">
                    <div className="flex items-center justify-center space-x-4 mb-3">
                      <h3 className="font-semibold text-slate-300">Staff Members</h3>
                      <div>{syncing ? <span className="text-xs text-slate-400">Syncing...</span> : <RefreshCcw size={14} className="cursor-pointer" onClick={handleSync} />}</div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      {staffUsers.map((staff) => (
                        <label key={staff.staff_id} className="flex items-center gap-2 text-sm text-slate-300">
                          <input type="checkbox" checked={assignedStaff.some((assigned) => assigned.staff_id === staff.staff_id)} onChange={(e) => handleStaffTemplateChange(template.id, staff.staff_id, e.target.checked)} className="cursor-pointer" />
                          {staff.staff_name}
                        </label>
                      ))}
                    </div>
                  </div>

                  {loadingGroups[template.id] ? (
                    <p className="text-slate-400">Loading groups and fields...</p>
                  ) : (
                    groups.map((group) => {
                      const allFieldsVisible = (group.fields || []).length ? group.fields.every((field) => field.isVisible) : group.isVisible;

                      return (
                        <div key={group.groupKey} className="rounded-lg shadow-sm mb-3 bg-slate-900 overflow-hidden border border-color-lines">
                          <div className="flex items-center justify-between px-4 py-3">
                            <div className="flex items-center space-x-3">
                              <input type="checkbox" checked={!!allFieldsVisible} onChange={(e) => handleGroupVisibilityChange(template.id, group, e.target.checked)} className="cursor-pointer" />
                              <button onClick={() => toggleGroup(template.id, group.groupKey)} className="flex items-center space-x-2 text-white cursor-pointer">
                                <ChevronDown size={18} className={`ease-in-out duration-150 ${group.open ? "-rotate-90" : "rotate-0"}`} />
                                <h2 className="font-semibold text-md">{group.groupLabel || group.groupKey}</h2>
                              </button>
                            </div>
                          </div>

                          <div className={`transition-all duration-300 ${group.open ? "max-h-auto p-4" : "max-h-0 p-0"} overflow-hidden`}>
                            {group.fields.map((field) => (
                              <div key={field.fieldKey} className="mb-3 text-left flex space-x-4 items-center text-slate-300">
                                <input type="checkbox" checked={field.isVisible} onChange={(e) => handleFieldVisibilityChange(template.id, group.groupKey, field.fieldKey, e.target.checked)} className="cursor-pointer" />
                                <label className="block font-medium">{field.fieldLabel || field.fieldKey}</label>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAddTemplate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-back-dark rounded-xl p-5 w-full max-w-md border border-color-lines">
            <h2 className="font-semibold text-lg mb-4">Add New Template</h2>
            <input type="text" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} placeholder="Template Name" className="w-full bg-slate-900 text-white border border-color-lines rounded-lg px-3 py-2 mb-4" />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowAddTemplate(false)} className="px-4 py-2 rounded-lg border border-color-lines" disabled={savingTemplate}>
                Cancel
              </button>
              <button onClick={handleAddTemplate} className="px-4 py-2 rounded-lg bg-blue-700 text-white" disabled={savingTemplate}>
                {savingTemplate ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTemplateId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-back-dark rounded-xl p-5 w-full max-w-md border border-color-lines">
            <h2 className="font-semibold text-lg mb-2">Delete Template</h2>

            <p className="text-slate-400 mb-4">Are you sure you want to delete this template?</p>

            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTemplateId(null)} className="px-4 py-2 border border-color-lines rounded-lg">
                Cancel
              </button>

              <button onClick={handleDeleteTemplate} className="px-4 py-2 bg-red-600 text-white rounded-lg">
                {deletingTemplate ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HiddenFields;
